import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, extractError } from '@/lib/api.js';
import { TEMAS } from '@shared/conselheiro.js';
import { Dica } from '@/components/Dica.js';

/**
 * O plano do ciclo: as 4 Metas e os indicadores mensais. O Conselheiro
 * Principal define; sócios e responsáveis acompanham e informam o valor do
 * mês de cada indicador.
 */

export type Plano = {
  quem: 'CONSELHEIRO' | 'SOCIO' | 'RESPONSAVEL';
  ciclo: {
    id: string;
    inicio_em: string;
    fim_em: string | null;
    mes: number;
  } | null;
  metas: {
    tipo: 'FINANCEIRA' | 'OPERACIONAL' | 'PESSOAS' | 'PESSOAL';
    nome: string;
    definida: boolean;
    descricao: string | null;
    unidade: string | null;
    linha_base: number | null;
    alvo: number | null;
    atual: number | null;
    atualizada_em: string | null;
    progresso: number | null;
  }[];
  indicadores: {
    id: string;
    nome: string;
    unidade: string | null;
    tema: number | null;
    faixa_min: number | null;
    faixa_max: number | null;
    ativo: boolean;
    valores: {
      competencia: string;
      valor: number;
      comentario: string | null;
      fora_da_faixa: boolean;
    }[];
  }[];
};

export const chaveDoPlano = (empresaId: string) => ['conselheiro-plano', empresaId];

export function usePlano(empresaId: string) {
  return useQuery({
    queryKey: chaveDoPlano(empresaId),
    queryFn: async () => (await api.get<Plano>(`/conselheiro/empresas/${empresaId}/plano`)).data,
  });
}

const fmt = (n: number | null, unidade?: string | null) =>
  n == null ? '—' : `${n.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}${unidade ? ` ${unidade}` : ''}`;
const mes = (iso: string) =>
  new Date(iso).toLocaleDateString('pt-BR', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
/** "1.234,5" ou "1234.5" → número; vazio → null; texto inválido → NaN. */
const paraNumero = (v: string) => {
  const bruto = v.trim();
  const t = bruto.includes(',') ? bruto.replace(/\./g, '').replace(',', '.') : bruto;
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
};
const mesAtual = () => new Date().toISOString().slice(0, 7);

const campo = 'rounded-element border border-ha-border2 bg-transparent px-2 py-1.5 text-sm';
const botao = 'rounded-element px-3 py-1.5 text-[10px] font-bold uppercase text-black disabled:opacity-50';

/** Campo com rótulo curto e, se houver, a dica no ícone ao lado. */
function Rotulado({
  rotulo,
  dica,
  children,
  className,
}: {
  rotulo: string;
  dica?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`flex flex-col gap-1 ${className ?? ''}`}>
      <span className="flex items-center gap-1 text-[11px] font-medium text-ha-muted">
        {rotulo}
        {dica ? <Dica texto={dica} rotulo={`Ajuda: ${rotulo}`} /> : null}
      </span>
      {children}
    </label>
  );
}

function Titulo({ children, dica }: { children: ReactNode; dica?: string }) {
  return (
    <h3 className="mb-2 flex items-center gap-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-ha-muted">
      {children}
      {dica ? <Dica texto={dica} /> : null}
    </h3>
  );
}

export function PlanoDoCiclo({ empresaId, podeDefinir }: { empresaId: string; podeDefinir: boolean }) {
  const q = usePlano(empresaId);
  if (q.isLoading) return <p className="text-sm text-ha-subtle">Carregando o plano…</p>;
  if (q.isError || !q.data) return <p className="text-sm text-red-600">{extractError(q.error).message}</p>;
  const p = q.data;
  if (!p.ciclo)
    return <p className="text-sm text-ha-subtle">Sem ciclo ativo. O plano é definido na S0, com o ciclo aberto.</p>;
  return (
    <div className="flex flex-col gap-6">
      <div>
        <Titulo dica="Uma meta de cada tipo por ciclo, definida na S0 com os sócios.">As 4 Metas do ciclo</Titulo>
        <div className="grid gap-3 md:grid-cols-2">
          {p.metas.map((m) => (
            <CartaoDaMeta key={m.tipo} empresaId={empresaId} meta={m} podeDefinir={podeDefinir} />
          ))}
        </div>
      </div>
      <Indicadores empresaId={empresaId} plano={p} podeDefinir={podeDefinir} />
    </div>
  );
}

function CartaoDaMeta({
  empresaId,
  meta,
  podeDefinir,
}: {
  empresaId: string;
  meta: Plano['metas'][number];
  podeDefinir: boolean;
}) {
  const qc = useQueryClient();
  const [editando, setEditando] = useState(false);
  const [f, setF] = useState({
    descricao: '',
    unidade: '',
    linha_base: '',
    alvo: '',
    atual: '',
  });
  const [erro, setErro] = useState<string | null>(null);

  const salvar = useMutation({
    mutationFn: async () => {
      const nums = {
        linha_base: paraNumero(f.linha_base),
        alvo: paraNumero(f.alvo),
        atual: paraNumero(f.atual),
      };
      if (Object.values(nums).some((n) => Number.isNaN(n))) throw new Error('Use só números em base, alvo e atual.');
      return (
        await api.put<Plano>(`/conselheiro/empresas/${empresaId}/metas/${meta.tipo}`, {
          descricao: f.descricao,
          unidade: f.unidade.trim() || null,
          ...nums,
        })
      ).data;
    },
    onSuccess: (dados) => {
      qc.setQueryData(chaveDoPlano(empresaId), dados);
      setEditando(false);
    },
    onError: (e) => setErro(e instanceof Error && !('response' in e) ? e.message : extractError(e).message),
  });

  const abrir = () => {
    const s = (n: number | null) => (n == null ? '' : String(n).replace('.', ','));
    setF({
      descricao: meta.descricao ?? '',
      unidade: meta.unidade ?? '',
      linha_base: s(meta.linha_base),
      alvo: s(meta.alvo),
      atual: s(meta.atual),
    });
    setErro(null);
    setEditando(true);
  };

  return (
    <div className="rounded-card border border-ha-border2 p-3">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-ha-muted">{meta.nome}</span>
        {podeDefinir && !editando ? (
          <button type="button" className="text-xs text-accent underline" onClick={abrir}>
            {meta.definida ? 'Editar' : 'Definir'}
          </button>
        ) : null}
      </div>
      {editando ? (
        <div className="flex flex-col gap-2">
          <Rotulado
            rotulo="Meta"
            dica="Uma frase clara, que os sócios reconheçam como deles: o que se quer alcançar até o fim do ciclo."
          >
            <textarea
              className={campo}
              rows={2}
              placeholder="O que se quer alcançar no ciclo"
              value={f.descricao}
              onChange={(e) => setF({ ...f, descricao: e.target.value })}
            />
          </Rotulado>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Rotulado rotulo="Unidade" dica="Ex.: R$ mil, %, dias, pessoas.">
              <input className={campo} value={f.unidade} onChange={(e) => setF({ ...f, unidade: e.target.value })} />
            </Rotulado>
            <Rotulado rotulo="Base" dica="Onde a empresa está no começo do ciclo.">
              <input
                className={campo}
                inputMode="decimal"
                value={f.linha_base}
                onChange={(e) => setF({ ...f, linha_base: e.target.value })}
              />
            </Rotulado>
            <Rotulado rotulo="Alvo" dica="Onde quer estar no fim do ciclo. Pode ser menor que a base (prazo, custo).">
              <input
                className={campo}
                inputMode="decimal"
                value={f.alvo}
                onChange={(e) => setF({ ...f, alvo: e.target.value })}
              />
            </Rotulado>
            <Rotulado rotulo="Atual" dica="O valor mais recente. Atualize nas manhãs.">
              <input
                className={campo}
                inputMode="decimal"
                value={f.atual}
                onChange={(e) => setF({ ...f, atual: e.target.value })}
              />
            </Rotulado>
          </div>
          {erro ? <p className="text-xs text-red-600">{erro}</p> : null}
          <div className="flex gap-2">
            <button
              type="button"
              className={botao}
              style={{ background: 'var(--primary)' }}
              disabled={salvar.isPending}
              onClick={() => salvar.mutate()}
            >
              Salvar
            </button>
            <button type="button" className="text-xs text-ha-muted underline" onClick={() => setEditando(false)}>
              Cancelar
            </button>
          </div>
        </div>
      ) : meta.definida ? (
        <>
          <p className="text-sm text-ha-text">{meta.descricao}</p>
          <p className="mt-1 text-xs text-ha-muted">
            de {fmt(meta.linha_base, meta.unidade)} para {fmt(meta.alvo, meta.unidade)} · atual{' '}
            {fmt(meta.atual, meta.unidade)}
          </p>
          {meta.progresso != null ? (
            <div
              className="mt-2 h-1.5 overflow-hidden rounded bg-[var(--border)]"
              title={`${Math.round(meta.progresso * 100)}% do caminho`}
            >
              <div
                className="h-full"
                style={{
                  width: `${meta.progresso * 100}%`,
                  background: 'var(--primary)',
                }}
              />
            </div>
          ) : null}
        </>
      ) : (
        <p className="text-sm text-ha-subtle">Ainda não definida.</p>
      )}
    </div>
  );
}

function Indicadores({ empresaId, plano, podeDefinir }: { empresaId: string; plano: Plano; podeDefinir: boolean }) {
  const qc = useQueryClient();
  const [novo, setNovo] = useState({
    nome: '',
    unidade: '',
    tema: '',
    faixa_min: '',
    faixa_max: '',
  });
  const [criando, setCriando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const ativos = plano.indicadores.filter((i) => i.ativo);

  const criar = useMutation({
    mutationFn: async () => {
      const faixa_min = paraNumero(novo.faixa_min);
      const faixa_max = paraNumero(novo.faixa_max);
      if (Number.isNaN(faixa_min) || Number.isNaN(faixa_max)) throw new Error('A faixa aceita só números.');
      return (
        await api.post<Plano>(`/conselheiro/empresas/${empresaId}/indicadores`, {
          nome: novo.nome,
          unidade: novo.unidade.trim() || null,
          tema: novo.tema === '' ? null : Number(novo.tema),
          faixa_min,
          faixa_max,
        })
      ).data;
    },
    onSuccess: (dados) => {
      qc.setQueryData(chaveDoPlano(empresaId), dados);
      setNovo({
        nome: '',
        unidade: '',
        tema: '',
        faixa_min: '',
        faixa_max: '',
      });
      setCriando(false);
    },
    onError: (e) => setErro(e instanceof Error && !('response' in e) ? e.message : extractError(e).message),
  });

  const desativar = useMutation({
    mutationFn: async (id: string) =>
      (
        await api.patch<Plano>(`/conselheiro/indicadores/${id}`, {
          ativo: false,
        })
      ).data,
    onSuccess: (dados) => qc.setQueryData(chaveDoPlano(empresaId), dados),
  });

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <Titulo dica="Poucos números, reportados uma vez por mês pela empresa. Fora da faixa, o conselheiro é alertado.">
          Indicadores do mês
        </Titulo>
        {podeDefinir && !criando ? (
          <button type="button" className="text-xs text-accent underline" onClick={() => setCriando(true)}>
            Novo indicador
          </button>
        ) : null}
      </div>
      {criando ? (
        <div className="mb-3 grid gap-2 rounded-card border border-ha-border2 p-3 sm:grid-cols-[2fr_1fr_1.4fr_1fr_1fr]">
          <Rotulado
            rotulo="Nome"
            dica="Um número que a empresa consegue levantar todo mês, sem esforço. Ex.: conversão de propostas, margem, prazo de entrega."
          >
            <input
              className={campo}
              placeholder="Ex.: Conversão de propostas"
              value={novo.nome}
              onChange={(e) => setNovo({ ...novo, nome: e.target.value })}
            />
          </Rotulado>
          <Rotulado rotulo="Unidade" dica="Ex.: %, R$ mil, dias.">
            <input
              className={campo}
              value={novo.unidade}
              onChange={(e) => setNovo({ ...novo, unidade: e.target.value })}
            />
          </Rotulado>
          <Rotulado
            rotulo="Tema"
            dica="O tema do radar que este indicador comprova. Ele aparece primeiro na hora de citar evidência."
          >
            <select className={campo} value={novo.tema} onChange={(e) => setNovo({ ...novo, tema: e.target.value })}>
              <option value="">—</option>
              {TEMAS.map((t, i) => (
                <option key={t} value={i}>
                  {t}
                </option>
              ))}
            </select>
          </Rotulado>
          <Rotulado rotulo="Mínimo" dica="Abaixo disto, o valor aparece em vermelho. Deixe vazio se não houver.">
            <input
              className={campo}
              inputMode="decimal"
              value={novo.faixa_min}
              onChange={(e) => setNovo({ ...novo, faixa_min: e.target.value })}
            />
          </Rotulado>
          <Rotulado rotulo="Máximo" dica="Acima disto, o valor aparece em vermelho. Deixe vazio se não houver.">
            <input
              className={campo}
              inputMode="decimal"
              value={novo.faixa_max}
              onChange={(e) => setNovo({ ...novo, faixa_max: e.target.value })}
            />
          </Rotulado>
          {erro ? <p className="text-xs text-red-600 sm:col-span-5">{erro}</p> : null}
          <div className="flex gap-2 sm:col-span-5">
            <button
              type="button"
              className={botao}
              style={{ background: 'var(--primary)' }}
              disabled={criar.isPending || novo.nome.trim().length < 2}
              onClick={() => criar.mutate()}
            >
              Criar
            </button>
            <button type="button" className="text-xs text-ha-muted underline" onClick={() => setCriando(false)}>
              Cancelar
            </button>
          </div>
        </div>
      ) : null}
      {ativos.length === 0 ? (
        <p className="text-sm text-ha-subtle">
          {podeDefinir
            ? 'Nenhum indicador ainda. Defina na S0 de três a cinco números que mostrem a saúde da empresa.'
            : 'O conselheiro ainda não definiu os indicadores.'}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {ativos.map((i) => (
            <LinhaDoIndicador
              key={i.id}
              empresaId={empresaId}
              indicador={i}
              podeDefinir={podeDefinir}
              aoDesativar={() => desativar.mutate(i.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function LinhaDoIndicador({
  empresaId,
  indicador: i,
  podeDefinir,
  aoDesativar,
}: {
  empresaId: string;
  indicador: Plano['indicadores'][number];
  podeDefinir: boolean;
  aoDesativar: () => void;
}) {
  const qc = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [competencia, setCompetencia] = useState(mesAtual());
  const [valor, setValor] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const ultimo = i.valores[0];

  const informar = useMutation({
    mutationFn: async () => {
      const v = paraNumero(valor);
      if (v == null || Number.isNaN(v)) throw new Error('Informe um número.');
      return (
        await api.post<Plano>(`/conselheiro/indicadores/${i.id}/valores`, {
          competencia,
          valor: v,
        })
      ).data;
    },
    onSuccess: (dados) => {
      qc.setQueryData(chaveDoPlano(empresaId), dados);
      setAberto(false);
      setValor('');
    },
    onError: (e) => setErro(e instanceof Error && !('response' in e) ? e.message : extractError(e).message),
  });

  const faixa =
    i.faixa_min != null && i.faixa_max != null
      ? `faixa ${fmt(i.faixa_min, null)} a ${fmt(i.faixa_max, null)}`
      : i.faixa_min != null
        ? `mín. ${fmt(i.faixa_min, null)}`
        : i.faixa_max != null
          ? `máx. ${fmt(i.faixa_max, null)}`
          : null;

  return (
    <li className="rounded-card border border-ha-border2 p-3 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <span className="font-medium text-ha-text">{i.nome}</span>{' '}
          <span className="text-xs text-ha-muted">
            {[i.tema != null ? TEMAS[i.tema] : null, faixa].filter(Boolean).join(' · ')}
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          {ultimo ? (
            <span className={ultimo.fora_da_faixa ? 'font-semibold text-red-600' : 'text-ha-text'}>
              {fmt(ultimo.valor, i.unidade)} <span className="text-ha-muted">em {mes(ultimo.competencia)}</span>
              {ultimo.fora_da_faixa ? ' · fora da faixa' : ''}
            </span>
          ) : (
            <span className="text-ha-muted">sem valor ainda</span>
          )}
          {!aberto ? (
            <button
              type="button"
              className="text-accent underline"
              onClick={() => {
                setErro(null);
                setAberto(true);
              }}
            >
              Informar mês
            </button>
          ) : null}
          {podeDefinir ? (
            <button
              type="button"
              className="text-ha-muted underline"
              onClick={() => window.confirm(`Desativar "${i.nome}"?`) && aoDesativar()}
            >
              Desativar
            </button>
          ) : null}
        </div>
      </div>
      {i.valores.length > 1 ? (
        <p className="mt-1 text-xs text-ha-muted">
          {i.valores
            .slice(1)
            .map((v) => `${mes(v.competencia)}: ${fmt(v.valor, null)}`)
            .join(' · ')}
        </p>
      ) : null}
      {aberto ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            type="month"
            aria-label="Mês de referência"
            className={campo}
            value={competencia}
            onChange={(e) => setCompetencia(e.target.value)}
          />
          <Dica texto="O mês a que o valor se refere. Informar o mesmo mês de novo substitui o valor." />
          <input
            className={`${campo} w-32`}
            inputMode="decimal"
            placeholder={i.unidade ?? 'valor'}
            value={valor}
            onChange={(e) => setValor(e.target.value)}
          />
          <button
            type="button"
            className={botao}
            style={{ background: 'var(--primary)' }}
            disabled={informar.isPending}
            onClick={() => informar.mutate()}
          >
            Salvar
          </button>
          <button type="button" className="text-xs text-ha-muted underline" onClick={() => setAberto(false)}>
            Cancelar
          </button>
          {erro ? <span className="text-xs text-red-600">{erro}</span> : null}
        </div>
      ) : null}
    </li>
  );
}
